import { create } from 'zustand';
import type { IApiUserInfoResp } from '@/services/swagger/easesourcing-auth/models';
import { getUserInfoGet } from '@/services/swagger/easesourcing-auth/UserLoginService';
import { logoutPost } from '@/services/swagger/easesourcing-auth/UserAuthService';
import { profileGetPost } from '@/services/swagger/easourcing/UserProfileService';
import { IApiUserProfileResp } from '@/services/swagger/easourcing/models';
import {
  clearAuthCookie,
  clearOAuthRedirectFlag,
  clearVisitorId,
  getCookie,
  OAUTH_REDIRECT_FLAG_KEY,
  VISITOR_ID_COOKIE_KEY,
} from '@/utils/auth';
import { authChannel } from '@/hooks/useAuthChannel';
import { destroyWebSocket, webSocketService } from '@/services/websocket';

/** 从 IApiUserInfoResp 获取展示名称 */
export function getDisplayName(user: IApiUserInfoResp | null): string {
  if (!user) return 'User';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const emailPrefix = user.email ? user.email.split('@')[0] || '' : '';
  return name || emailPrefix || 'User';
}

interface UserState {
  user: IApiUserInfoResp | null;
  fetchProfileLoading: boolean;
  userProfileData: IApiUserProfileResp | null;
  loading: boolean;
  displayName: string;
  fetchUser: (options?: { isAfterLoginSuccess?: boolean }) => Promise<void>;
  clearUser: () => void;
  /** 统一的本地清理（不调后端 logout 接口），清除 user/cookie/ws/广播登出 */
  cleanupAuth: () => void;
  logout: (reloadPage?: boolean) => Promise<void>;
  fetchUserProfile: () => Promise<IApiUserProfileResp | null>;
  reloadUserProfile: () => Promise<IApiUserProfileResp | null>;
}

let fetchProfilePromiseCache: Promise<IApiUserProfileResp | null> | null = null;
const useUserStore = create<UserState>((set, get) => ({
  user: null,
  loading: false,
  userProfileData: null,
  fetchProfileLoading: true,
  displayName: '',
  fetchUser: async (options?: { isAfterLoginSuccess?: boolean }) => {
    const { fetchUserProfile } = get();
    const isAfterLoginSuccess = options?.isAfterLoginSuccess ?? false;
    set({ loading: true });
    try {
      const result = await getUserInfoGet();
      const data = result?.data;
      if (data?.isLogin) {
        set({ user: data });
        // OAuth 回调后首次成功拉取用户时，广播登录成功（通知其他 Tab）
        if (typeof window !== 'undefined' && sessionStorage.getItem(OAUTH_REDIRECT_FLAG_KEY)) {
          authChannel.broadcastAuthSuccess();
          clearOAuthRedirectFlag();
        }
        // 登录成功后：仅在「登录成功后的第一次调用」时执行 visitor 对比
        // getUserInfoGet 若 visitor_id 不在 cookie 里，后端会 set 新的，故需在接口返回后读取
        const visitorId = getCookie(VISITOR_ID_COOKIE_KEY);
        if (isAfterLoginSuccess && visitorId && visitorId.trim() !== '' && data.userId && visitorId !== data.userId) {
          clearVisitorId();
          setTimeout(() => {
            const isHomePage = window.location.pathname === '/' || window.location.pathname === '';
            if (isHomePage) {
              window.location.reload();
            } else {
              window.location.href = '/';
            }
          }, 500);
          return;
        }
        // 获取到登录后的用户信息，删除本地 cookie 中的访客 id
        if (visitorId && visitorId.trim() !== '') {
          clearVisitorId();
        }
        // 登录后立即获取用户信息
        await fetchUserProfile();
        // 登录成功后用 userId 作为 deviceId；initialize 内部会检测 deviceId 变更并自动 reinitialize
        const wsDeviceId = data.userId || getCookie(VISITOR_ID_COOKIE_KEY);
        if (wsDeviceId) {
          webSocketService.initialize(wsDeviceId);
          webSocketService.connect().catch(err => {
            console.error('WebSocket reconnection after login failed:', err);
          });
        }
      } else {
        set({ user: null });
      }
    } catch {
      set({ user: null });
    } finally {
      set({ loading: false });
    }
  },
  clearUser: () => {
    clearOAuthRedirectFlag();
    clearVisitorId();
    set({ user: null });
  },
  cleanupAuth: () => {
    destroyWebSocket();
    authChannel.broadcastAuthLogout();
    clearAuthCookie();
    clearOAuthRedirectFlag();
    clearVisitorId();
    fetchProfilePromiseCache = null;
    set({ user: null, userProfileData: null, displayName: '' });
  },
  logout: async (reloadPage = false) => {
    const { cleanupAuth } = get();
    try {
      await logoutPost();
      if (reloadPage) {
        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      }
    } catch {
      // 即使接口失败也继续本地清理，保证用户能退出
    } finally {
      cleanupAuth();
    }
  },
  fetchUserProfile: async () => {
    if (!fetchProfilePromiseCache) {
      fetchProfilePromiseCache = (async () => {
        try {
          set({ fetchProfileLoading: true });
          const res = await profileGetPost();
          set({ userProfileData: res?.data || null });
          // update display name
          const { user } = get();
          const displayName = res?.data?.name || getDisplayName(user);
          set({ displayName });
          return res?.data || null;
        } catch (error) {
          fetchProfilePromiseCache = null; // 允许下次重新尝试获取数据
          console.error('Failed to fetch user profile:', error);
          return null;
        } finally {
          set({ fetchProfileLoading: false });
        }
      })();
    }
    return fetchProfilePromiseCache;
  },
  reloadUserProfile: () => {
    const { fetchUserProfile } = get();
    fetchProfilePromiseCache = null;
    set({ userProfileData: null });
    return fetchUserProfile();
  },
}));

export default useUserStore;
